import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Pencil, Trash2 } from "lucide-react";
import api from "../lib/api";
import { fmtDate, inr, num, pct } from "../lib/format";
import { Loading, Empty } from "../components/kit";
import { Button } from "../components/ui/button";
import DeleteEntryDialog from "../components/DeleteEntryDialog";
import { LEAVE_LABEL } from "../components/DayStatusPanel";

const BADGE = {
  submitted: "bg-green-50 text-green-700 border-green-200",
  week_off: "bg-slate-100 text-slate-600 border-slate-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-[#F7F3EA] text-[#8a6d2f] border-[#E6D9B8]",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

function StatusRow({ s }) {
  const key = s.status === "week_off" ? "week_off" : s.leave_status;
  return (
    <div className="bs-card p-4 flex items-center justify-between" data-testid={`status-row-${s.date}`}>
      <div>
        <div className="font-semibold text-[#011E38]">{fmtDate(s.date)}</div>
        {s.reason && <div className="text-xs text-slate-500 mt-0.5">“{s.reason}”</div>}
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs font-semibold border rounded-full px-3 py-1 ${BADGE[key]}`}>{s.status === "week_off" ? "Week Off" : LEAVE_LABEL[s.leave_status]}</span>
        <Link to={`/entry?date=${s.date}`} className="text-slate-500 hover:text-[#011E38]" data-testid={`status-edit-${s.date}`}><Pencil className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}

function EntryRow({ e, onDelete }) {
  return (
    <div className="bs-card p-4" data-testid={`entry-row-${e.date}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-[#011E38]">{fmtDate(e.date)}</div>
        <div className="flex items-center gap-2">
          {e.closed ? <span className="text-xs text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Closed</span> : (
            <>
              <span className={`text-xs font-semibold border rounded-full px-3 py-1 ${e.no_walkin ? BADGE.week_off : BADGE.submitted}`} data-testid={`entry-badge-${e.date}`}>{e.no_walkin ? "No Walk-in" : "Submitted"}</span>
              <Button asChild variant="ghost" size="sm" className="h-8 px-2"><Link to={`/entry?date=${e.date}`} data-testid={`entry-edit-${e.date}`}><Pencil className="w-4 h-4" /></Link></Button>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-red-700" onClick={() => onDelete(e)} data-testid={`entry-delete-${e.date}`}><Trash2 className="w-4 h-4" /></Button>
            </>
          )}
        </div>
      </div>
      {e.no_walkin ? <div className="text-sm text-slate-500 mt-2">Worked · no customer attended (all KPIs ₹0)</div> : (
      <div className="flex items-end justify-between mt-2">
        <div className="num text-2xl font-bold text-[#011E38]">{inr(e.sales)}</div>
        <div className="text-xs text-slate-500 text-right">
          {num(e.walkins)} walk-ins · {num(e.bills)} bills · {pct(e.walkins ? (e.bills / e.walkins) * 100 : 0)}
          <br />GMS {inr(e.gms)}
        </div>
      </div>)}
    </div>
  );
}

export default function MyEntries() {
  const [rows, setRows] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => Promise.all([api.get("/entries"), api.get("/day-status/mine")]).then(([e, s]) =>
    setRows([...e.data.map((x) => ({ ...x, kind: "entry" })), ...s.data.map((x) => ({ ...x, kind: "status" }))].sort((a, b) => b.date.localeCompare(a.date)))), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  return (
    <div className="space-y-5 bs-rise">
      <div>
        <div className="bs-eyebrow">This month</div>
        <h1 className="font-display text-4xl font-extrabold text-[#011E38] tracking-tight mt-1">My Entries</h1>
      </div>
      {!rows.length && <Empty>No entries yet. <Link to="/entry" className="underline">Add today's entry</Link></Empty>}
      <div className="space-y-2.5" data-testid="my-entries-list">
        {rows.map((r) => r.kind === "entry" ? <EntryRow key={r.id} e={r} onDelete={setDel} /> : <StatusRow key={r.id} s={r} />)}
      </div>
      {del && <DeleteEntryDialog entry={del} onClose={() => setDel(null)} onDone={() => { setDel(null); load(); }} />}
    </div>
  );
}
